/**
 * AgentEditorModal - Full-screen modal for creating/editing agents WITH TABS.
 * 
 * Features:
 * - Tab-based layout (Général, Base de connaissances, Mémoire)
 * - Model selection, RAG settings, knowledge base
 * - Icon upload
 * - System prompt override (admin only)
 * - Memory settings with scope selection
 * 
 * @accessibility WCAG 2.1 AA (focus trap, ARIA, keyboard nav)
 */
import React, { useState, useCallback, useEffect, useId } from 'react'
import { X, Save, Loader2, AlertTriangle, Info, Upload, Brain, BookOpen, Settings, Sparkles, Users } from 'lucide-react'
import { api, systemPromptsApi, SystemPromptListItem, memoryApi, MemoryScope } from '../lib/api'
import type { LlmModelOption } from '../types/llmModel'
import AgentModelPickerModal from './AgentModelPickerModal'
import { HelpTooltip } from './Tooltip'
import AgentTypeSelector from './AgentTypeSelector'
import TemplateLibrary from './TemplateLibrary'
import { AgentType, PromptTemplate } from '../data/promptTemplates'
import { useAppLanguage } from '../i18n'

// =============================================================================
// Types
// =============================================================================

type Subject = { id: number; title: string; source: string }

interface AgentFormData {
  name: string
  subtitle: string
  instructions: string
  messageLimit: string
  modelId: string
  topK: string
  rerankTopN: string
  disableOwnKnowledge: boolean
  subjectIds: number[]
  teamId: number | null
  systemPromptId: number | null
  memoryEnabled: boolean
  memoryEndUserEnabled: boolean
  memoryScopeIds: number[]
}

interface AgentData {
  id: number
  name: string
  slug: string
  subtitle?: string | null
  instructions?: string | null
  message_limit?: number | null
  model_id?: string | null
  top_k?: number | null
  rerank_top_n: number
  disable_own_knowledge: boolean
  subject_ids: number[]
  team_id?: number | null
  system_prompt_id?: number | null
  memory_enabled: boolean
  memory_end_user_enabled: boolean
}

interface AgentEditorModalProps {
  agent: AgentData | null
  subjects: Subject[]
  subjectsLoading: boolean
  llmModels: LlmModelOption[]
  llmModelsLoading: boolean
  isAdmin: boolean
  systemPrompts: SystemPromptListItem[]
  teams: { id: number; title: string }[]
  teamsLoading: boolean
  onClose: (saved: boolean) => void
  onSave: (agent: AgentData) => void
}

const DEFAULT_FORM: AgentFormData = {
  name: '',
  subtitle: '',
  instructions: '',
  messageLimit: '',
  modelId: '',
  topK: '10',
  rerankTopN: '5',
  disableOwnKnowledge: false,
  subjectIds: [],
  teamId: null,
  systemPromptId: null,
  memoryEnabled: true,
  memoryEndUserEnabled: false,
  memoryScopeIds: [],
}

// =============================================================================
// Component
// =============================================================================

export default function AgentEditorModal({
  agent,
  subjects,
  subjectsLoading,
  llmModels,
  llmModelsLoading,
  isAdmin,
  systemPrompts,
  teams,
  teamsLoading,
  onClose,
  onSave,
}: AgentEditorModalProps) {
  const formId = useId()
  const { t } = useAppLanguage()
  const isEditMode = !!agent

  // Form state
  const [formData, setFormData] = useState<AgentFormData>(() => {
    if (agent) {
      return {
        name: agent.name,
        subtitle: agent.subtitle || '',
        instructions: agent.instructions || '',
        messageLimit: agent.message_limit?.toString() || '',
        modelId: agent.model_id || '',
        topK: agent.top_k?.toString() || '10',
        rerankTopN: agent.rerank_top_n?.toString() || '5',
        disableOwnKnowledge: agent.disable_own_knowledge,
        subjectIds: agent.subject_ids || [],
        teamId: agent.team_id ?? null,
        systemPromptId: agent.system_prompt_id || null,
        memoryEnabled: agent.memory_enabled ?? true,
        memoryEndUserEnabled: agent.memory_end_user_enabled ?? false,
        memoryScopeIds: [],
      }
    }
    return DEFAULT_FORM
  })

  const [iconFile, setIconFile] = useState<File | null>(null)
  const [iconPreview, setIconPreview] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [availableScopes, setAvailableScopes] = useState<MemoryScope[]>([])
  const [loadingScopes, setLoadingScopes] = useState(false)
  const [activeTab, setActiveTab] = useState<'general' | 'knowledge' | 'memory'>('general')
  const [selectedAgentType, setSelectedAgentType] = useState<AgentType | null>(null)
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false)
  const [modelPickerOpen, setModelPickerOpen] = useState(false)

  // Handle form changes
  const handleChange = useCallback((field: keyof AgentFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (error) setError(null)
  }, [error])

  // Handle subject toggle
  const handleSubjectToggle = useCallback((subjectId: number) => {
    setFormData(prev => ({
      ...prev,
      subjectIds: prev.subjectIds.includes(subjectId)
        ? prev.subjectIds.filter(id => id !== subjectId)
        : [...prev.subjectIds, subjectId]
    }))
  }, [])

  // Handle icon file change
  const handleIconChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setIconFile(file)
      const reader = new FileReader()
      reader.onloadend = () => setIconPreview(reader.result as string)
      reader.readAsDataURL(file)
    }
  }, [])

  // Handle submit
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name.trim()) {
      setError(t.agents.agentEditor.errorNameRequired)
      return
    }

    setSaving(true)
    setError(null)

    try {
      const payload: Record<string, any> = {
        name: formData.name.trim(),
        subtitle: formData.subtitle.trim() || null,
        instructions: formData.instructions.trim() || null,
        message_limit: formData.messageLimit ? parseInt(formData.messageLimit) : null,
        model_id: formData.modelId || null,
        top_k: formData.topK ? parseInt(formData.topK) : 10,
        rerank_top_n: formData.rerankTopN ? parseInt(formData.rerankTopN) : 5,
        disable_own_knowledge: formData.disableOwnKnowledge,
        subject_ids: formData.subjectIds,
        memory_enabled: formData.memoryEnabled,
        memory_end_user_enabled: formData.memoryEndUserEnabled,
      }

      // Team assignment (0 = remove from team)
      if (formData.teamId !== null) {
        payload.team_id = formData.teamId
      } else if (isEditMode && agent?.team_id) {
        // If editing and team was removed, send 0 to clear
        payload.team_id = 0
      }

      // Admin-only: system prompt override
      if (isAdmin && formData.systemPromptId !== null) {
        payload.system_prompt_id = formData.systemPromptId
      }

      let savedAgent: AgentData

      if (isEditMode && agent) {
        const { data } = await api.patch(`/agents/${agent.id}`, payload)
        savedAgent = data
      } else {
        const { data } = await api.post('/agents', payload)
        savedAgent = data
      }

      // Upload icon if selected
      if (iconFile && savedAgent.id) {
        const iconFormData = new FormData()
        iconFormData.append('file', iconFile)
        try {
          await api.post(`/agents/${savedAgent.id}/icon`, iconFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        } catch (iconErr) {
          console.warn('Failed to upload icon:', iconErr)
        }
      }

      // Save memory scope assignments
      if (savedAgent.id && formData.memoryEnabled) {
        try {
          await memoryApi.updateAgentScopes(savedAgent.id, formData.memoryScopeIds)
        } catch (scopeErr) {
          console.warn('Failed to update memory scopes:', scopeErr)
        }
      }

      onSave(savedAgent)
      onClose(true)
    } catch (err: any) {
      const rawDetail = err?.response?.data?.detail
      // Handle object error responses (quota errors have {error, message, ...})
      const detail = typeof rawDetail === 'object' && rawDetail !== null
        ? rawDetail.message || rawDetail.error || JSON.stringify(rawDetail)
        : rawDetail || err?.message || t.agents.agentEditor.errorSave
      setError(detail)
    } finally {
      setSaving(false)
    }
  }, [formData, isEditMode, agent, iconFile, isAdmin, onSave, onClose])

  // Load available scopes and agent's assigned scopes
  useEffect(() => {
    const loadScopes = async () => {
      setLoadingScopes(true)
      try {
        // Load all available scopes
        const scopesRes = await memoryApi.listScopes()
        setAvailableScopes(scopesRes.items.filter(s => s.scope_type === 'tenant'))
        
        // Load agent's assigned scopes if editing
        if (agent?.id) {
          const assignedScopeIds = await memoryApi.getAgentScopes(agent.id)
          setFormData(prev => ({ ...prev, memoryScopeIds: assignedScopeIds }))
        }
      } catch (err) {
        console.error('Failed to load scopes:', err)
      } finally {
        setLoadingScopes(false)
      }
    }
    
    loadScopes()
  }, [agent?.id])

  // Handle scope toggle
  const handleScopeToggle = useCallback((scopeId: number) => {
    setFormData(prev => ({
      ...prev,
      memoryScopeIds: prev.memoryScopeIds.includes(scopeId)
        ? prev.memoryScopeIds.filter(id => id !== scopeId)
        : [...prev.memoryScopeIds, scopeId]
    }))
  }, [])

  // Handle agent type selection
  const handleAgentTypeChange = useCallback((type: AgentType | null) => {
    setSelectedAgentType(type)
    setShowTemplateLibrary(!!type)
  }, [])

  // Handle template selection
  const handleTemplateSelect = useCallback((template: PromptTemplate) => {
    handleChange('instructions', template.content)
    setShowTemplateLibrary(false)
  }, [handleChange])

  // ESC to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <div 
      className="fixed inset-0 z-50 bg-zinc-950"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${formId}-title`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800 bg-zinc-900">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-900/30">
            <Sparkles className="w-5 h-5 text-emerald-400" />
          </div>
          <h2 id={`${formId}-title`} className="text-lg font-semibold text-white">
            {isEditMode ? `${t.agents.agentEditor.titleEdit}: ${agent?.name}` : t.agents.agentEditor.titleNew}
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => onClose(false)}
            className="px-4 py-2 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors"
          >
            {t.agents.agentEditor.cancel}
          </button>
          <button
            type="submit"
            form={formId}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-colors disabled:opacity-50"
            disabled={saving}
            data-onboarding={isEditMode ? 'agent-save-btn' : 'agent-create-btn'}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t.agents.agentEditor.saving}
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditMode ? t.agents.agentEditor.save : t.agents.agentEditor.create}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mx-6 mt-4 p-3 rounded-lg bg-red-900/20 border border-red-800 text-red-300 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-zinc-800 bg-zinc-900 px-6">
        <div className="flex gap-1">
          <button
            type="button"
            onClick={() => setActiveTab('general')}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'general'
                ? 'border-emerald-500 text-emerald-400'
                : 'border-transparent text-zinc-400 hover:text-zinc-300'
            }`}
          >
            {t.agents.agentEditor.tabGeneral}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('knowledge')}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'knowledge'
                ? 'border-emerald-500 text-emerald-400'
                : 'border-transparent text-zinc-400 hover:text-zinc-300'
            }`}
          >
            {t.agents.agentEditor.tabKnowledge}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('memory')}
            className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'memory'
                ? 'border-emerald-500 text-emerald-400'
                : 'border-transparent text-zinc-400 hover:text-zinc-300'
            }`}
          >
            {t.agents.agentEditor.tabMemory}
          </button>
        </div>
      </div>

      {/* Body */}
      <form 
        id={formId}
        onSubmit={handleSubmit}
        className="h-[calc(100vh-180px)] overflow-y-auto p-6"
      >
        {/* Tab: Général */}
        {activeTab === 'general' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {/* Column 1: Basic Info */}
            <div className="space-y-6">
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
                <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  {t.agents.agentEditor.sectionGeneral}
                </h3>
                
                <div className="space-y-4">
                  {/* Name */}
                  <div>
                    <label htmlFor={`${formId}-name`} className="block text-sm font-medium text-zinc-300 mb-1">
                      {t.agents.agentEditor.nameLabel} <span className="text-red-400">*</span>
                    </label>
                    <input
                      id={`${formId}-name`}
                      type="text"
                      value={formData.name}
                      onChange={e => handleChange('name', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                      placeholder={t.agents.agentEditor.namePlaceholder}
                      required
                      data-onboarding="agent-name-input"
                    />
                  </div>

                  {/* Subtitle */}
                  <div>
                    <label htmlFor={`${formId}-subtitle`} className="block text-sm font-medium text-zinc-300 mb-1">
                      {t.agents.agentEditor.subtitleLabel}
                    </label>
                    <input
                      id={`${formId}-subtitle`}
                      type="text"
                      value={formData.subtitle}
                      onChange={e => handleChange('subtitle', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                      placeholder={t.agents.agentEditor.subtitlePlaceholder}
                      data-onboarding="agent-subtitle-input"
                    />
                  </div>

                  {/* Icon */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      {t.agents.agentEditor.iconLabel}
                    </label>
                    <div className="flex items-center gap-4">
                      {iconPreview ? (
                        <img src={iconPreview} alt="Preview" className="w-16 h-16 rounded-xl object-cover border border-zinc-700" />
                      ) : (
                        <div className="w-16 h-16 rounded-xl bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                          <Upload className="w-6 h-6 text-zinc-500" />
                        </div>
                      )}
                      <label className="flex-1">
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/webp,image/svg+xml"
                          onChange={handleIconChange}
                          className="hidden"
                        />
                        <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors cursor-pointer text-sm">
                          <Upload className="w-4 h-4" />
                          {t.agents.agentEditor.chooseImage}
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* Agent Type Selector */}
                  <AgentTypeSelector
                    value={selectedAgentType}
                    onChange={handleAgentTypeChange}
                  />

                  {/* Template Library */}
                  {showTemplateLibrary && selectedAgentType && (
                    <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
                      <TemplateLibrary
                        agentType={selectedAgentType}
                        currentPrompt={formData.instructions}
                        onSelectTemplate={handleTemplateSelect}
                      />
                    </div>
                  )}

                  {/* Instructions */}
                  <div>
                    <label htmlFor={`${formId}-instructions`} className="block text-sm font-medium text-zinc-300 mb-1">
                      {t.agents.agentEditor.instructionsLabel}
                    </label>
                    <textarea
                      id={`${formId}-instructions`}
                      value={formData.instructions}
                      onChange={e => handleChange('instructions', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 min-h-[120px]"
                      placeholder={t.agents.agentEditor.instructionsPlaceholder}
                      data-onboarding="agent-instructions-input"
                    />
                    {formData.instructions && (
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setShowTemplateLibrary(!showTemplateLibrary)}
                          className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
                        >
                          {showTemplateLibrary ? t.agents.agentEditor.hideTemplates : t.agents.agentEditor.changeTemplate}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Message Limit */}
                  <div>
                    <label htmlFor={`${formId}-msglimit`} className="block text-sm font-medium text-zinc-300 mb-1">
                      {t.agents.agentEditor.messageLimitLabel}
                    </label>
                    <input
                      id={`${formId}-msglimit`}
                      type="number"
                      min={1}
                      value={formData.messageLimit}
                      onChange={e => handleChange('messageLimit', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                      placeholder={t.agents.agentEditor.messageLimitPlaceholder}
                    />
                    <p className="mt-1 text-xs text-zinc-500">{t.agents.agentEditor.messageLimitHelp}</p>
                  </div>
                </div>
              </section>
            </div>

            {/* Column 2: AI Settings */}
            <div className="space-y-6">
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
                <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Brain className="w-4 h-4" />
                  {t.agents.agentEditor.sectionAI}
                </h3>
                
                <div className="space-y-4">
                  {/* Model */}
                  <div>
                    <label htmlFor={`${formId}-model`} className="block text-sm font-medium text-zinc-300 mb-1 flex items-center gap-1">
                      {t.agents.agentEditor.modelLabel}
                      <HelpTooltip content="Le modèle de langage utilisé pour générer les réponses" position="right" />
                    </label>
                    <div className="flex gap-2">
                      <input
                        id={`${formId}-model`}
                        readOnly
                        className="min-w-0 flex-1 cursor-default px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100"
                        value={
                          formData.modelId
                            ? llmModels.find(m => m.name === formData.modelId)?.name || formData.modelId
                            : t.agents.agentEditor.modelDefault
                        }
                      />
                      <button
                        type="button"
                        onClick={() => setModelPickerOpen(true)}
                        disabled={llmModelsLoading}
                        className="shrink-0 rounded-full border border-emerald-700/60 bg-emerald-950/40 px-4 py-2 text-xs font-semibold text-emerald-200 transition hover:bg-emerald-900/50 disabled:opacity-50"
                      >
                        {t.agents.modelPicker.browse}
                      </button>
                    </div>
                    <AgentModelPickerModal
                      open={modelPickerOpen}
                      onClose={() => setModelPickerOpen(false)}
                      models={llmModels}
                      value={formData.modelId}
                      onApply={name => handleChange('modelId', name)}
                      loading={llmModelsLoading}
                      defaultOptionLabel={t.agents.agentEditor.modelDefault}
                    />
                  </div>

                  {/* Top K */}
                  <div>
                    <label htmlFor={`${formId}-topk`} className="block text-sm font-medium text-zinc-300 mb-1 flex items-center gap-1">
                      {t.agents.agentEditor.topKLabel}
                      <HelpTooltip content="Nombre de chunks récupérés pour le contexte" position="right" />
                    </label>
                    <input
                      id={`${formId}-topk`}
                      type="number"
                      min={1}
                      max={50}
                      value={formData.topK}
                      onChange={e => handleChange('topK', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                      placeholder="10"
                    />
                    <p className="mt-1 text-xs text-zinc-500">{t.agents.agentEditor.topKHelp}</p>
                  </div>

                  {/* Rerank Top N */}
                  <div>
                    <label htmlFor={`${formId}-rerank`} className="block text-sm font-medium text-zinc-300 mb-1 flex items-center gap-1">
                      {t.agents.agentEditor.rerankLabel}
                      <HelpTooltip content="Résultats finaux après reranking sémantique" position="right" />
                    </label>
                    <input
                      id={`${formId}-rerank`}
                      type="number"
                      min={1}
                      max={20}
                      value={formData.rerankTopN}
                      onChange={e => handleChange('rerankTopN', e.target.value)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                      placeholder="5"
                    />
                    <p className="mt-1 text-xs text-zinc-500">{t.agents.agentEditor.rerankHelp}</p>
                  </div>

                  {/* Strict mode */}
                  <div className="pt-2">
                    <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-zinc-700 hover:border-zinc-600 transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.disableOwnKnowledge}
                        onChange={e => handleChange('disableOwnKnowledge', e.target.checked)}
                        className="mt-0.5 w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-emerald-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-zinc-200 flex items-center gap-2">
                          {t.agents.agentEditor.strictLabel}
                        </span>
                        <p className="text-xs text-zinc-500 mt-1">
                          {t.agents.agentEditor.strictHelp}
                        </p>
                      </div>
                    </label>
                  </div>
                </div>
              </section>

              {/* Team Assignment */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
                <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {t.agents.agentEditor.sectionTeam}
                </h3>
                
                <div>
                  <label htmlFor={`${formId}-team`} className="block text-sm font-medium text-zinc-300 mb-1">
                    {t.agents.agentEditor.teamLabel}
                  </label>
                  <select
                    id={`${formId}-team`}
                    value={formData.teamId ?? ''}
                    onChange={e => handleChange('teamId', e.target.value ? parseInt(e.target.value) : null)}
                    disabled={teamsLoading}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 disabled:opacity-50"
                    data-onboarding="agent-team-select"
                  >
                    <option value="">{t.agents.agentEditor.teamNone}</option>
                    {teams.map(team => (
                      <option key={team.id} value={team.id}>{team.title}</option>
                    ))}
                  </select>
                </div>
              </section>

              {/* Admin: System Prompt Override */}
              {isAdmin && (
                <section className="bg-zinc-900 border border-amber-800/50 rounded-xl p-5">
                  <h3 className="text-sm font-medium text-amber-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                    {t.agents.agentEditor.sectionAdminPrompt}
                  </h3>
                  
                  <div>
                    <label htmlFor={`${formId}-sysprompt`} className="block text-sm font-medium text-zinc-300 mb-1">
                      {t.agents.agentEditor.adminPromptLabel}
                    </label>
                    <select
                      id={`${formId}-sysprompt`}
                      value={formData.systemPromptId || ''}
                      onChange={e => handleChange('systemPromptId', e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                    >
                      <option value="">{t.agents.agentEditor.adminPromptDefault}</option>
                      {systemPrompts.map(sp => (
                        <option key={sp.id} value={sp.id}>{sp.name}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-amber-400/70">
                      {t.agents.agentEditor.adminPromptHelp}
                    </p>
                  </div>
                </section>
              )}
            </div>
          </div>
        )}

        {/* Tab: Base de connaissances */}
        {activeTab === 'knowledge' && (
          <div className="max-w-4xl mx-auto">
            <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                {t.agents.agentEditor.sectionKnowledge}
              </h3>
              
              <p className="text-xs text-zinc-500 mb-4">
                {t.agents.agentEditor.knowledgeHelp}
              </p>

              <div className="space-y-2">
                {subjectsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
                  </div>
                ) : subjects.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500 text-sm">
                    <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>{t.agents.agentEditor.noKnowledge}</p>
                    <p className="text-xs mt-1">{t.agents.agentEditor.noKnowledgeHint}</p>
                  </div>
                ) : (
                  subjects.map(subject => {
                    const checked = formData.subjectIds.includes(subject.id)
                    return (
                      <label
                        key={subject.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          checked 
                            ? 'bg-emerald-900/20 border-emerald-700' 
                            : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleSubjectToggle(subject.id)}
                          className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-emerald-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-zinc-200 truncate">{subject.title}</div>
                          <div className="text-xs text-zinc-500 truncate">{subject.source}</div>
                        </div>
                      </label>
                    )
                  })
                )}
              </div>

              {/* Selected count */}
              <div className="mt-4 pt-4 border-t border-zinc-800">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">{t.agents.agentEditor.selected}</span>
                  <span className="font-medium text-emerald-400">
                    {formData.subjectIds.length} / {subjects.length}
                  </span>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* Tab: Mémoire */}
        {activeTab === 'memory' && (
          <div className="max-w-4xl mx-auto">
            <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                {t.agents.agentEditor.sectionMemory}
              </h3>
              
              <div className="space-y-4">
                <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border border-zinc-700 hover:border-zinc-600 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.memoryEnabled}
                    onChange={e => handleChange('memoryEnabled', e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-emerald-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-zinc-200 flex items-center gap-2">
                      {t.agents.agentEditor.memoryBusiness}
                    </span>
                    <p className="text-xs text-zinc-500 mt-1">
                      {t.agents.agentEditor.memoryBusinessHelp}
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border border-zinc-700 hover:border-zinc-600 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.memoryEndUserEnabled}
                    onChange={e => handleChange('memoryEndUserEnabled', e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-emerald-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-zinc-200 flex items-center gap-2">
                      {t.agents.agentEditor.memoryEndUser}
                    </span>
                    <p className="text-xs text-zinc-500 mt-1">
                      {t.agents.agentEditor.memoryEndUserHelp}
                    </p>
                  </div>
                </label>

                {formData.memoryEnabled && (
                  <div className="mt-6 p-5 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-medium text-zinc-300">
                        {t.agents.agentEditor.scopesTitle}
                      </h4>
                      {loadingScopes && (
                        <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
                      )}
                    </div>
                    
                    {availableScopes.length === 0 && !loadingScopes ? (
                      <p className="text-sm text-zinc-500">
                        {t.agents.agentEditor.noScopes} <a href="/memory" className="underline hover:text-zinc-400">{t.agents.agentEditor.scopeMemoryLink}</a>.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {availableScopes.map(scope => (
                          <label
                            key={scope.id}
                            className="flex items-start gap-3 p-3 rounded-lg hover:bg-zinc-700/30 cursor-pointer transition-colors border border-zinc-700/50"
                          >
                            <input
                              type="checkbox"
                              checked={formData.memoryScopeIds.includes(scope.id)}
                              onChange={() => handleScopeToggle(scope.id)}
                              className="mt-0.5 w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-emerald-500"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-zinc-200">
                                {scope.name}
                              </div>
                              {scope.description && (
                                <div className="text-xs text-zinc-500 mt-1">
                                  {scope.description}
                                </div>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                    
                    <div className="mt-4 p-3 bg-blue-900/20 border border-blue-800/30 rounded-lg">
                      <p className="text-xs text-blue-400">
                        {formData.memoryScopeIds.length === 0 
                          ? t.agents.agentEditor.scopeNoneSelected
                          : t.agents.agentEditor.scopeSelected.replace('{n}', String(formData.memoryScopeIds.length))
                        }
                      </p>
                    </div>
                  </div>
                )}

                <div className="mt-4 p-4 bg-blue-900/20 border border-blue-800/30 rounded-lg">
                  <p className="text-sm text-blue-400">
                    {t.agents.agentEditor.memoryPageHint} <a href="/memory" className="underline hover:text-blue-300">{t.agents.agentEditor.scopeMemoryLink}</a>
                  </p>
                </div>
              </div>
            </section>
          </div>
        )}
      </form>
    </div>
  )
}
